import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  format,
  parseISO,
  isSameDay,
  isSameMonth,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addMonths,
  subMonths,
  isBefore,
  startOfDay,
} from "date-fns";
import { fi as fiLocale, sv as svLocale } from "date-fns/locale";
import {
  Calendar as CalendarIcon,
  Lock,
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  Clock,
  Send,
  X as XIcon,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// weekStartsOn = 1 (Monday). date-fns treats Monday as first day of week.
const WEEK_OPTS = { weekStartsOn: 1 };

const emptyForm = { startTime: "09:00", endTime: "15:00", title: "" };

export default function ProfileAvailability({ profile }) {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [monthAnchor, setMonthAnchor] = useState(() => startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState(() => startOfDay(new Date()));
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const showCalendar = !!profile?.id;
  const isMe = user?.id === profile?.id;
  const dateLocale = lang === "fi" ? fiLocale : lang === "sv" ? svLocale : undefined;

  const load = useCallback(async () => {
    if (!showCalendar) { setLoading(false); return; }
    try {
      const r = await api.get(`/users/${profile.id}/availability`);
      setData(r.data);
    } catch (err) {
      console.error("[availability] load failed", err);
      setData({ slots: [] });
    } finally {
      setLoading(false);
    }
  }, [profile?.id, showCalendar]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30 seconds so both owners and visitors see near-real-time updates
  useEffect(() => {
    if (!showCalendar) return;
    const id = setInterval(() => { load(); }, 30_000);
    return () => clearInterval(id);
  }, [load, showCalendar]);

  const [bookingSlot, setBookingSlot] = useState(null);
  const [bookingNotes, setBookingNotes] = useState("");
  const [bookingBusy, setBookingBusy] = useState(false);

  const submitBooking = async () => {
    if (!bookingSlot || !user) return;
    setBookingBusy(true);
    try {
      await api.post("/bookings", {
        slot_id: bookingSlot.id,
        notes: bookingNotes || null,
      });
      toast.success(t("booking_requested"));
      setBookingSlot(null);
      setBookingNotes("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
    } finally {
      setBookingBusy(false);
    }
  };

  // Build the 6-row calendar grid for the current month anchor
  const gridDays = useMemo(() => {
    const from = startOfWeek(startOfMonth(monthAnchor), WEEK_OPTS);
    const to = endOfWeek(endOfMonth(monthAnchor), WEEK_OPTS);
    return eachDayOfInterval({ start: from, end: to });
  }, [monthAnchor]);

  const slotsByDay = useMemo(() => {
    const map = new Map();
    for (const s of data?.slots || []) {
      try {
        const key = format(parseISO(s.start), "yyyy-MM-dd");
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(s);
      } catch (err) {
        console.error("[availability] bad slot", err);
      }
    }
    return map;
  }, [data]);

  const respondToRequest = async (bookingId, status) => {
    try {
      await api.patch(`/bookings/${bookingId}/status`, { status });
      toast.success(status === "accepted" ? t("request_accepted") : t("request_declined"));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
    }
  };

  const dayKey = (d) => format(d, "yyyy-MM-dd");
  const today = startOfDay(new Date());
  const daySlots = slotsByDay.get(dayKey(selectedDay)) || [];

  if (!showCalendar) return null;

  const addSlot = async (e) => {
    e.preventDefault();
    if (!isMe) return;
    if (!form.startTime || !form.endTime) return;
    setSaving(true);
    try {
      const dateStr = format(selectedDay, "yyyy-MM-dd");
      const startISO = new Date(`${dateStr}T${form.startTime}:00`).toISOString();
      const endISO = new Date(`${dateStr}T${form.endTime}:00`).toISOString();
      if (new Date(endISO) <= new Date(startISO)) {
        toast.error(t("time_end_after_start"));
        setSaving(false);
        return;
      }
      await api.post("/slots", {
        start: startISO,
        end: endISO,
        title: form.title || null,
      });
      toast.success(t("slot_added"));
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
    } finally {
      setSaving(false);
    }
  };

  const removeSlot = async (slotId) => {
    if (!isMe) return;
    if (!window.confirm(t("confirm_remove_slot"))) return;
    try {
      await api.delete(`/slots/${slotId}`);
      toast.success(t("slot_removed"));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
    }
  };

  const monthLabel = format(monthAnchor, "LLLL yyyy", { locale: dateLocale });
  const localizedMonthLabel = monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);

  return (
    <div data-testid="profile-availability" className="mt-12">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-[#143122]">{t("availability")}</div>
          <h2 className="mt-2 font-serif-display text-3xl text-[#1A1F1C]">
            {t("availability_title")}
          </h2>
          <p className="mt-2 text-sm text-[#1A1F1C]/70 max-w-lg">
            {isMe ? t("availability_sub_owner") : t("availability_sub")}
          </p>
        </div>
      </div>

      {/* Month header */}
      <div className="mt-6 border border-[#E6E2D8] rounded-lg overflow-hidden bg-white">
        <div className="flex items-center justify-between px-4 py-3 bg-[#F4F1E9] border-b border-[#E6E2D8]">
          <button
            type="button"
            data-testid="cal-prev-month"
            onClick={() => setMonthAnchor((m) => subMonths(m, 1))}
            className="p-1.5 rounded-md hover:bg-white transition-colors"
            aria-label={t("prev")}
          >
            <ChevronLeft size={16} strokeWidth={1.5} />
          </button>
          <div
            data-testid="cal-month-label"
            className="font-serif-display text-lg text-[#143122]"
          >
            {localizedMonthLabel}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              data-testid="cal-today-btn"
              onClick={() => {
                const t0 = startOfDay(new Date());
                setMonthAnchor(startOfMonth(t0));
                setSelectedDay(t0);
              }}
              className="text-xs px-2 py-1 rounded-md border border-[#143122]/30 text-[#143122] hover:bg-white transition-colors"
            >
              {t("today")}
            </button>
            <button
              type="button"
              data-testid="cal-next-month"
              onClick={() => setMonthAnchor((m) => addMonths(m, 1))}
              className="p-1.5 rounded-md hover:bg-white transition-colors"
              aria-label={t("next")}
            >
              <ChevronRight size={16} strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Weekday header */}
        <div className="grid grid-cols-7 border-b border-[#E6E2D8]">
          {WEEKDAY_LABELS.map((wd, i) => (
            <div
              key={i}
              className="text-[10px] uppercase tracking-wider text-[#1A1F1C]/50 px-2 py-2 text-center border-r border-[#E6E2D8] last:border-r-0"
            >
              {t(`wd_${wd.toLowerCase()}`)}
            </div>
          ))}
        </div>

        {/* Day grid */}
        <div className="grid grid-cols-7">
          {gridDays.map((d, i) => {
            const key = dayKey(d);
            const inMonth = isSameMonth(d, monthAnchor);
            const isToday = isSameDay(d, today);
            const isSelected = isSameDay(d, selectedDay);
            const isPast = isBefore(d, today);
            const dSlots = slotsByDay.get(key) || [];
            const openCount = dSlots.filter((s) => s.status === "available").length;
            const bookedCount = dSlots.filter((s) => s.status === "booked").length;
            const pendingCount = isMe
              ? dSlots.reduce((n, s) => n + (s.pending_requests?.length || 0), 0)
              : 0;
            const isLastCol = (i + 1) % 7 === 0;
            const isLastRow = i >= gridDays.length - 7;
            return (
              <button
                key={key}
                type="button"
                data-testid={`cal-day-${key}`}
                onClick={() => setSelectedDay(d)}
                className={`min-h-[72px] p-2 text-left transition-colors relative ${
                  isLastCol ? "" : "border-r"
                } ${isLastRow ? "" : "border-b"} border-[#E6E2D8] ${
                  isSelected
                    ? "bg-[#143122] text-white"
                    : inMonth
                    ? isPast
                      ? "bg-[#FAFAFA] hover:bg-[#F4F1E9]"
                      : "bg-white hover:bg-[#F4F1E9]"
                    : "bg-[#FAFAFA]/50 text-[#1A1F1C]/30 hover:bg-[#F4F1E9]"
                }`}
              >
                <div
                  className={`text-sm font-medium ${
                    isSelected
                      ? "text-white"
                      : isToday
                      ? "text-[#143122] font-bold"
                      : inMonth
                      ? "text-[#1A1F1C]"
                      : "text-[#1A1F1C]/30"
                  }`}
                >
                  {format(d, "d")}
                </div>
                {isToday && !isSelected && (
                  <div className="absolute top-1 right-1.5 w-1.5 h-1.5 rounded-full bg-[#143122]" />
                )}
                <div className="mt-1 space-y-0.5">
                  {openCount > 0 && (
                    <div
                      data-testid={`cal-day-${key}-open`}
                      className={`inline-block text-[10px] px-1.5 py-0.5 rounded ${
                        isSelected
                          ? "bg-white/20 text-white"
                          : "bg-[#143122]/10 text-[#143122]"
                      }`}
                    >
                      {openCount} {t("open_short")}
                    </div>
                  )}
                  {bookedCount > 0 && (
                    <div
                      className={`inline-block text-[10px] px-1.5 py-0.5 rounded ${
                        isSelected
                          ? "bg-red-500/30 text-white"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {bookedCount} {t("booked_short")}
                    </div>
                  )}
                  {pendingCount > 0 && (
                    <div
                      data-testid={`cal-day-${key}-pending`}
                      className={`inline-block text-[10px] px-1.5 py-0.5 rounded font-medium ${
                        isSelected
                          ? "bg-[#B58A2F] text-white"
                          : "bg-[#B58A2F]/15 text-[#B58A2F]"
                      }`}
                    >
                      {pendingCount} {t("pending_short")}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected day details */}
      <div className="mt-6">
        <div className="flex items-center gap-2 text-sm font-medium text-[#143122]">
          <CalendarIcon size={14} strokeWidth={1.5} />
          {format(selectedDay, "EEEE d MMMM yyyy", { locale: dateLocale })}
        </div>

        {/* Slots list */}
        <div className="mt-3">
          {loading ? (
            <div className="text-sm text-[#1A1F1C]/60">Loading…</div>
          ) : daySlots.length === 0 ? (
            <div
              data-testid="cal-empty"
              className="text-sm text-[#1A1F1C]/60 border border-dashed border-[#E6E2D8] rounded-lg py-6 text-center"
            >
              {t("no_slots_this_day")}
            </div>
          ) : (
            <ul className="grid sm:grid-cols-2 gap-2">
              {daySlots.map((s) => {
                const start = parseISO(s.start);
                const end = parseISO(s.end);
                const timeLabel = `${format(start, "HH:mm")} – ${format(end, "HH:mm")}`;
                if (s.status === "available") {
                  const pending = (isMe && s.pending_requests) || [];
                  return (
                    <li key={s.id} data-testid={`slot-open-${s.id}`}>
                      {isMe ? (
                        <div className="border border-[#143122]/30 rounded-lg p-3 bg-[#F4F1E9] space-y-3">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <div className="flex items-center gap-2 text-sm font-medium text-[#143122]">
                                <Clock size={12} strokeWidth={1.75} /> {timeLabel}
                              </div>
                              <span className="mt-1 inline-block text-[10px] uppercase tracking-wider text-[#143122]">
                                {t("open")}
                              </span>
                              {s.title && <div className="mt-1 text-xs text-[#1A1F1C]/70">{s.title}</div>}
                            </div>
                            <button
                              type="button"
                              data-testid={`slot-remove-${s.id}`}
                              onClick={() => removeSlot(s.id)}
                              className="text-[#1A1F1C]/50 hover:text-[#7A2A2A] transition-colors"
                              aria-label={t("delete")}
                            >
                              <Trash2 size={14} strokeWidth={1.5} />
                            </button>
                          </div>
                          {pending.length > 0 && (
                            <div className="border-t border-[#B58A2F]/30 pt-3 space-y-2">
                              <div className="text-[10px] uppercase tracking-wider text-[#B58A2F] font-medium">
                                {pending.length} {t("pending_requests")}
                              </div>
                              {pending.map((req) => (
                                <div
                                  key={req.id}
                                  data-testid={`pending-request-${req.id}`}
                                  className="bg-white border border-[#B58A2F]/30 rounded-md p-2.5"
                                >
                                  <div className="text-sm font-medium text-[#1A1F1C]">
                                    {req.client_name || t("unknown")}
                                  </div>
                                  {req.notes && (
                                    <div className="mt-1 text-xs text-[#1A1F1C]/70">{req.notes}</div>
                                  )}
                                  <div className="mt-2 flex gap-2">
                                    <button
                                      type="button"
                                      data-testid={`request-accept-${req.id}`}
                                      onClick={() => respondToRequest(req.id, "accepted")}
                                      className="text-xs px-3 py-1 rounded bg-[#143122] text-white hover:bg-[#0f2418]"
                                    >
                                      {t("accept")}
                                    </button>
                                    <button
                                      type="button"
                                      data-testid={`request-decline-${req.id}`}
                                      onClick={() => respondToRequest(req.id, "declined")}
                                      className="text-xs px-3 py-1 rounded border border-[#7A2A2A] text-[#7A2A2A] hover:bg-[#7A2A2A] hover:text-white"
                                    >
                                      {t("decline")}
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <button
                          type="button"
                          data-testid={`slot-book-btn-${s.id}`}
                          onClick={() => {
                            if (!user) {
                              toast.error(t("login_required"));
                              return;
                            }
                            setBookingSlot(s);
                          }}
                          className="w-full text-left block border border-[#143122]/30 rounded-lg p-3 bg-white hover:bg-[#F4F1E9] transition-colors"
                        >
                          <div className="flex items-center gap-2 text-sm font-medium text-[#143122]">
                            <Clock size={12} strokeWidth={1.75} /> {timeLabel}
                          </div>
                          <span className="mt-1 inline-block text-[10px] uppercase tracking-wider text-[#143122]">
                            {t("open")}
                          </span>
                          {s.title && <div className="mt-1 text-xs text-[#1A1F1C]/70">{s.title}</div>}
                          <div className="mt-2 text-xs text-[#143122] underline inline-flex items-center gap-1">
                            <Send size={11} strokeWidth={1.75} /> {t("request_booking")}
                          </div>
                        </button>
                      )}
                    </li>
                  );
                }
                const booking = s.booking;
                return (
                  <li key={s.id} data-testid={`slot-booked-${s.id}`}>
                    <div className="border border-red-300 rounded-lg p-3 bg-red-50">
                      <div className="flex items-center gap-2 text-sm font-medium text-red-800">
                        <Clock size={12} strokeWidth={1.75} /> {timeLabel}
                      </div>
                      <span className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-red-700">
                        <Lock size={10} strokeWidth={1.75} /> {t("booked")}
                      </span>
                      {booking ? (
                        <div className="mt-1 text-xs text-[#1A1F1C]/70">
                          {isMe ? (
                            <>{t("with")} {booking.client_name}{s.title ? ` · ${s.title}` : ""}</>
                          ) : (
                            <>{t("your_booking")}{s.title ? ` · ${s.title}` : ""}</>
                          )}
                        </div>
                      ) : (
                        <div className="mt-1 text-xs text-red-700/70">{t("private_details")}</div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Owner-only inline add form */}
        {isMe && (
          <form
            onSubmit={addSlot}
            data-testid="cal-add-slot-form"
            className="mt-4 border border-[#143122]/20 rounded-lg p-4 bg-white"
          >
            <div className="flex items-center gap-2 text-sm font-medium text-[#143122]">
              <Plus size={14} strokeWidth={1.75} /> {t("add_slot_on_day")}
            </div>
            <div className="mt-3 grid sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">{t("start_time")}</label>
                <input
                  data-testid="cal-slot-start"
                  required
                  type="time"
                  className="kav-input"
                  value={form.startTime}
                  onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">{t("end_time")}</label>
                <input
                  data-testid="cal-slot-end"
                  required
                  type="time"
                  className="kav-input"
                  value={form.endTime}
                  onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium mb-1">
                  {t("slot_title_label")}
                </label>
                <input
                  data-testid="cal-slot-title"
                  type="text"
                  placeholder={t("slot_title_placeholder")}
                  className="kav-input"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>
            </div>
            <button
              type="submit"
              data-testid="cal-add-slot-submit"
              disabled={saving}
              className="mt-3 kav-btn-primary text-sm"
            >
              <Plus size={12} strokeWidth={1.75} /> {saving ? "..." : t("add_slot")}
            </button>
          </form>
        )}
      </div>

      {/* Booking-request modal (visitors) */}
      {bookingSlot && (
        <div
          data-testid="booking-modal"
          className="fixed inset-0 z-[10004] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setBookingSlot(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-white rounded-2xl shadow-2xl"
          >
            <div className="flex items-start justify-between px-6 py-4 border-b border-[#E6E2D8]">
              <div>
                <div className="overline text-[#143122]">{t("booking_request")}</div>
                <h2 className="mt-1 font-serif-display text-xl text-[#143122]">
                  {profile.name}
                </h2>
                <div className="mt-1 text-sm text-[#1A1F1C]/70">
                  {format(parseISO(bookingSlot.start), "EEEE d MMMM yyyy · HH:mm", { locale: dateLocale })}
                  {" – "}
                  {format(parseISO(bookingSlot.end), "HH:mm", { locale: dateLocale })}
                </div>
                {bookingSlot.title && (
                  <div className="mt-1 text-xs text-[#1A1F1C]/60">{bookingSlot.title}</div>
                )}
              </div>
              <button
                type="button"
                data-testid="booking-modal-close"
                onClick={() => setBookingSlot(null)}
                className="text-[#1A1F1C]/50 hover:text-[#143122]"
                aria-label="Close"
              >
                <XIcon size={18} strokeWidth={1.75} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  {t("booking_notes_label")}
                </label>
                <textarea
                  data-testid="booking-notes-input"
                  rows={4}
                  className="kav-input"
                  placeholder={t("booking_notes_placeholder")}
                  value={bookingNotes}
                  onChange={(e) => setBookingNotes(e.target.value)}
                />
              </div>
              <div className="text-xs text-[#1A1F1C]/60 bg-[#F4F1E9] rounded p-2">
                {t("booking_notes_hint")}
              </div>
            </div>
            <div className="flex gap-3 p-6 pt-2 border-t border-[#E6E2D8]">
              <button
                type="button"
                data-testid="booking-modal-cancel"
                onClick={() => setBookingSlot(null)}
                className="flex-1 rounded-md border border-[#143122]/20 text-[#143122] hover:bg-[#F4F1E9] px-4 py-2 text-sm font-medium"
              >
                {t("cancel")}
              </button>
              <button
                type="button"
                data-testid="booking-modal-submit"
                disabled={bookingBusy}
                onClick={submitBooking}
                className="flex-1 kav-btn-primary text-sm justify-center disabled:opacity-50"
              >
                <Send size={12} strokeWidth={1.75} />
                {bookingBusy ? "..." : t("send_request")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
